import React, { useCallback, useRef, useState } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { useAriaLiveAnnouncer } from '@soomo/lib/components/AriaLiveAnnouncer/index';
import { ModalActionButton, WebtextButton } from '@soomo/lib/components/index';
import Modal from '@soomo/lib/components/Modal/index';

import * as styles from './styles';
import { statusTextHeadingId } from '../MCQuestionPoolList/utils';
import { ALL_CHAPTERS, useStudyStackStore } from '../store';
import { selectStatusText } from '../store/selectors';

const ChapterSelect: React.FC = () => {
	const {
		chapterFilter,
		setChapterFilter,
		chapters,
		chapterNamesIncludeNumbering,
		clearResponses
	} = useStudyStackStore(
		useShallow((state) => ({
			chapters: state.chapters,
			chapterFilter: state.chapterFilter,
			setChapterFilter: state.setChapterFilter,
			chapterNamesIncludeNumbering: state.config.chapter_names_include_numbering,
			clearResponses: state.clearResponses
		}))
	);

	const [selectedChapter, setSelectedChapter] = useState<string | null>(null);
	const [modalOpen, setModalOpen] = useState(false);
	const returnFocusRef = useRef<HTMLElement | null>(null);
	const { makePoliteAnnouncement } = useAriaLiveAnnouncer();

	const handleChangeSelectedChapter = useCallback(
		(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedChapter(e.target.value),
		[]
	);

	const handleModalCancelClick = useCallback(() => setModalOpen(false), []);

	const handleModalContinueClick = useCallback(() => {
		setChapterFilter(selectedChapter!);

		// Focus the status heading that begins the generated stack — a more sensible
		// landing spot than the "Generate My Study Stack" button, which is disabled
		// once its selection matches the applied filter.
		if (chapterFilter) {
			// A truthy `chapterFilter` means we came from the confirmation modal — it
			// only opens when a filter is already applied. Reset responses and let the
			// Modal restore focus to the heading after it closes.
			clearResponses();
			returnFocusRef.current = document.getElementById(statusTextHeadingId);
		} else {
			// First generation, no modal: the heading mounts on this update, so focus
			// it once it has rendered.
			requestAnimationFrame(() => document.getElementById(statusTextHeadingId)?.focus());
		}
		setModalOpen(false);

		// get new status text immediately, before next render
		const statusText = selectStatusText(useStudyStackStore.getState())!;
		// polite announcement + 500ms delay is to work around strangeness in Safari + VoiceOver
		// (normally we'd prefer to issue an assertive announcement immediately)
		setTimeout(() => makePoliteAnnouncement(statusText), 500);
	}, [chapterFilter, clearResponses, makePoliteAnnouncement, selectedChapter, setChapterFilter]);

	const handleGenerateClick = useCallback(() => {
		if (chapterFilter === selectedChapter) return;

		if (chapterFilter) {
			// Default to the browser's native focus restoration (the generate button)
			// if the user cancels; the Continue handler overrides this when needed.
			returnFocusRef.current = null;
			setModalOpen(true);
		} else {
			handleModalContinueClick();
		}
	}, [chapterFilter, handleModalContinueClick, selectedChapter]);

	return (
		<section css={styles.controlsSection}>
			<div css={styles.controls}>
				<div css={styles.chapterFilterSelectWrapper}>
					<select
						css={[styles.chapterFilterSelect, !selectedChapter && styles.noChapterSelected]}
						onChange={handleChangeSelectedChapter}
						aria-label="Select a chapter">
						{!selectedChapter && <option>Select a chapter</option>}
						<option value={ALL_CHAPTERS}>All chapters</option>
						{chapters.map((ch) => {
							const disabled = ch.available_question_pools === 0;
							return (
								<option key={ch.family_id} value={ch.family_id} disabled={disabled}>
									{/* some chapters like Introduction have a blank `chapter_number`, " " */}
									{!chapterNamesIncludeNumbering &&
										ch.chapter_number.trim() &&
										`${ch.chapter_number}: `}
									“{ch.name}”{disabled && ' (not yet available)'}
								</option>
							);
						})}
					</select>
				</div>
				<WebtextButton
					disabled={selectedChapter == null || selectedChapter === chapterFilter}
					onClick={handleGenerateClick}
					css={styles.generateButton}>
					Generate My Study Stack
				</WebtextButton>
			</div>

			<Modal
				id="generate-new-study-stack-confirmation"
				role="dialog"
				open={modalOpen}
				returnFocus={returnFocusRef}
				onClose={handleModalCancelClick}
				title="Update your study stack?"
				withFormClose={false}
				actions={
					<>
						<ModalActionButton autoFocus variant="outlined" onClick={handleModalCancelClick}>
							Cancel
						</ModalActionButton>
						<ModalActionButton onClick={handleModalContinueClick}>Continue</ModalActionButton>
					</>
				}>
				<p>
					When you generate a different set of questions, any study questions you’ve answered so far
					will be reset. Don’t worry! This won’t affect your webtext grade.
				</p>
				<p>Would you like to continue?</p>
			</Modal>
		</section>
	);
};

export default ChapterSelect;
